import { useEffect, useMemo } from 'react';

import { AnimatePresence } from 'framer-motion';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';

import { AuthPage } from 'components/pages/Admin/Auth';
import { PageEditor } from 'components/pages/Admin/Editor';
import { LoaderScreen } from 'components/pages/LoaderScreen';
import { NotFoundPage } from 'components/pages/NotFoundPage';
import { WelcomeScreen } from 'components/pages/WelcomeScreen';
import { DynamicPageRender } from 'components/templates/DynamicPageRender';
import { FadeAnimation } from 'components/templates/FadeWrapper';
import { AdminLayout } from 'components/templates/layouts/AdminLayout';
import { useIsAuthenticated } from 'hooks/api/auth/useIsAuthenticated';
import { usePageShareUrl } from 'hooks/api/builder/usePageShareUrl';
import { useMeta } from 'hooks/api/useMeta';

export const App = (): JSX.Element => {
    const { pathname } = useLocation();

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [pathname]);

    const { checkAuthStatus, authChecked, authenticated } =
        useIsAuthenticated();

    useEffect(() => {
        checkAuthStatus();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const {
        meta,
        loading: metaLoading,
        error,
        refresh,
    } = useMeta(authenticated ? 'pageList' : 'guestPageList');

    useEffect(() => {
        const timeout = setTimeout(() => {
            if (error) {
                refresh();
            }
        }, 3000);
        return () => {
            clearTimeout(timeout);
        };
    }, [error, refresh]);

    const loading = useMemo(() => !meta || !authChecked, [authChecked, meta]);

    const dynamicPages = useMemo(() => {
        return meta || [];
    }, [meta]);

    usePageShareUrl(dynamicPages, loading);

    return (
        <AnimatePresence>
            <Routes>
                {!loading ? (
                    <Route path="/">
                        <Route
                            index
                            element={
                                <FadeAnimation>
                                    <WelcomeScreen />
                                </FadeAnimation>
                            }
                        />
                        <Route
                            path="/report"
                            element={
                                <Navigate to="/doklad/o-doklade/" replace />
                            }
                        />
                        {dynamicPages.map((el) => (
                            <Route
                                key={`page_idx_${el.id}`}
                                path={el.href}
                                element={
                                    <DynamicPageRender
                                        pages={dynamicPages}
                                        metaLoading={metaLoading}
                                        metaError={error}
                                        pageID={el.id}
                                    />
                                }
                            />
                        ))}
                        <Route
                            path="*"
                            element={<Navigate to="/404" replace />}
                        />
                    </Route>
                ) : (
                    <Route path="*" element={<LoaderScreen />} />
                )}
                <Route path="/admin" element={<AdminLayout />}>
                    <Route path="auth" element={<AuthPage />} />
                    <Route path="content_manager" element={<PageEditor />} />
                    <Route
                        path="*"
                        element={<NotFoundPage backPath="/admin" />}
                    />
                </Route>
                <Route
                    path="/404"
                    element={
                        <FadeAnimation>
                            <NotFoundPage />
                        </FadeAnimation>
                    }
                />
            </Routes>
        </AnimatePresence>
    );
};
